/**
 * LeftShield Portal - Vulnerability Detail Page
 * Route: /security/vulnerabilities/:referenceId | Category: Risk
 *
 * §12746 Wave 2 — GUID-routed detail page for `Vulnerability`, replacing the local `DetailDrawer`
 * instances on `SecurityPage` (Vulnerabilities tab) AND `DependenciesPage` (Vulnerabilities tab,
 * same entity) for direct-link / deep-link access. Shape follows the reference migration
 * `src/pages/AIDecisionDetailPage.tsx`.
 *
 * FEATURE-PORTING PARITY — this page is the UNION of both source drawers (`SecurityPage.tsx` and
 * `DependenciesPage.tsx` render slightly different field sets for the same `Vulnerability`
 * entity); every field/section/badge from either is ported, nothing omitted:
 *   - Badges: severity, status, isKnownExploited (KEV)
 *   - Metadata grid: Identifier, Source, CVSS Score, EPSS Score, Affected Packages, Priority
 *   - Title
 *   - Description
 *   - CWE (DependenciesPage-only field — included here)
 *   - CVSS Vector
 *   - Metadata: First Seen, Last Seen, Published, KEV Due Date
 *   - Acknowledgement (DependenciesPage-only section — included here): Acknowledged By,
 *     Acknowledged At, Acknowledged Notes
 */
import React from 'react';
import { Title3, Body1, Body2, Caption1, Card, Badge, Button, Spinner, tokens } from '@fluentui/react-components';
import { ShieldErrorRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePageStyles } from '../../styles/page-styles';
import { PageHeader } from '../../components/common/PageHeader';
import { DetailSection, DetailField, DetailMetadataGrid, DetailBadges } from '../../components/common/DetailDrawer';
import { vulnerabilitiesApi } from '../../services/api';

/** Risk Red — `brand/brand-config.json` → `colors.categoryThemes.risk.primary`. */
const VULNERABILITY_COLOR = '#E85252';

const severityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const statusColor = (status: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status?.toLowerCase()) {
    case 'new':
    case 'open':
      return 'danger';
    case 'triaged':
    case 'inremediation':
    case 'inprogress':
      return 'warning';
    case 'resolved':
    case 'closed':
      return 'success';
    default:
      return 'informative';
  }
};

export const VulnerabilityDetailPage: React.FC = () => {
  const ps = usePageStyles();
  const { referenceId } = useParams<{ referenceId: string }>();
  const navigate = useNavigate();

  const {
    data: vulnerability,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['vulnerability-by-reference-id', referenceId],
    queryFn: () => vulnerabilitiesApi.getByReferenceId(referenceId!),
    enabled: !!referenceId,
  });

  const backAction = (
    <Button appearance="subtle" onClick={() => navigate('/security')}>
      Back to Security
    </Button>
  );

  if (!referenceId) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Vulnerability Detail"
          subtitle="No vulnerability reference ID was provided."
          icon={<ShieldErrorRegular />}
          iconColor={VULNERABILITY_COLOR}
          actions={backAction}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Vulnerability Detail"
          subtitle="Loading vulnerability…"
          icon={<ShieldErrorRegular />}
          iconColor={VULNERABILITY_COLOR}
        />
        <Card>
          <div className={ps.cardBody}>
            <Spinner label="Loading vulnerability…" />
          </div>
        </Card>
      </div>
    );
  }

  if (isError || !vulnerability) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Vulnerability Detail"
          subtitle={`Vulnerability ${referenceId}`}
          icon={<ShieldErrorRegular />}
          iconColor={VULNERABILITY_COLOR}
          actions={backAction}
        />
        <Card>
          <div className={ps.cardBody}>
            <ErrorCircleRegular style={{ fontSize: '32px', color: tokens.colorPaletteRedForeground1 }} />
            <Body1>Unable to load this vulnerability. It may not exist, or you may not have access to it.</Body1>
            <Button appearance="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const v = vulnerability;

  return (
    <div className={ps.page}>
      <PageHeader
        title="Vulnerability Detail"
        subtitle={v.identifier}
        icon={<ShieldErrorRegular />}
        iconColor={VULNERABILITY_COLOR}
        actions={backAction}
      />

      {/* Badges & metadata grid */}
      <Card>
        <div className={ps.cardBody}>
          <DetailBadges>
            <Badge appearance="filled" color={severityColor(v.severity)}>
              {v.severity}
            </Badge>
            <Badge appearance="tint" color={statusColor(v.status)}>
              {v.status}
            </Badge>
            {v.isKnownExploited && (
              <Badge appearance="filled" color="danger">
                KEV
              </Badge>
            )}
          </DetailBadges>

          <DetailMetadataGrid>
            <DetailField label="Identifier">
              <Body1 style={{ fontFamily: 'monospace', fontWeight: 500 }}>{v.identifier}</Body1>
            </DetailField>
            <DetailField label="Source">
              <Badge appearance="outline">{v.source}</Badge>
            </DetailField>
            <DetailField label="CVSS Score">
              <Body1>{v.cvssScore != null ? v.cvssScore.toFixed(1) : '-'}</Body1>
            </DetailField>
            <DetailField label="EPSS Score">
              <Body1>{v.epssScore != null ? `${(v.epssScore * 100).toFixed(2)}%` : '-'}</Body1>
            </DetailField>
            <DetailField label="Affected Packages">
              <Body1>{v.affectedPackageCount}</Body1>
            </DetailField>
            <DetailField label="Priority">
              <Body1>{v.priority}</Body1>
            </DetailField>
          </DetailMetadataGrid>

          {v.title && (
            <DetailField label="Title">
              <Title3>{v.title}</Title3>
            </DetailField>
          )}
        </div>
      </Card>

      {/* Description */}
      {v.description && (
        <Card>
          <div className={ps.cardBody}>
            <DetailSection title="Description">
              <Body1>{v.description}</Body1>
            </DetailSection>
          </div>
        </Card>
      )}

      {/* CWE + CVSS Vector */}
      {(v.cwe || v.cvssVector) && (
        <Card>
          <div className={ps.cardBody}>
            {v.cwe && (
              <DetailField label="CWE">
                <Body2 style={{ fontFamily: 'monospace' }}>{v.cwe}</Body2>
              </DetailField>
            )}
            {v.cvssVector && (
              <DetailField label="CVSS Vector">
                <Body2 style={{ fontFamily: 'monospace' }}>{v.cvssVector}</Body2>
              </DetailField>
            )}
          </div>
        </Card>
      )}

      {/* Metadata */}
      <Card>
        <div className={ps.cardBody}>
          <DetailSection>
            <DetailMetadataGrid>
              <DetailField label="First Seen">
                <Body2>{new Date(v.firstSeenAt).toLocaleString()}</Body2>
              </DetailField>
              <DetailField label="Last Seen">
                <Body2>{new Date(v.lastSeenAt).toLocaleString()}</Body2>
              </DetailField>
              {v.publishedAt && (
                <DetailField label="Published">
                  <Body2>{new Date(v.publishedAt).toLocaleString()}</Body2>
                </DetailField>
              )}
              {v.kevDueDate && (
                <DetailField label="KEV Due Date">
                  <Body2>{new Date(v.kevDueDate).toLocaleDateString()}</Body2>
                </DetailField>
              )}
            </DetailMetadataGrid>
          </DetailSection>
        </div>
      </Card>

      {/* Acknowledgement */}
      {v.acknowledgedBy && (
        <Card>
          <div className={ps.cardBody}>
            <DetailSection title="Acknowledgement">
              <DetailMetadataGrid>
                <DetailField label="Acknowledged By">
                  <Body2>{v.acknowledgedBy}</Body2>
                </DetailField>
                {v.acknowledgedAt && (
                  <DetailField label="Acknowledged At">
                    <Body2>{new Date(v.acknowledgedAt).toLocaleString()}</Body2>
                  </DetailField>
                )}
              </DetailMetadataGrid>
              {v.acknowledgedNotes && <Body2>{v.acknowledgedNotes}</Body2>}
            </DetailSection>
          </div>
        </Card>
      )}

      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Reference ID: {v.referenceId}</Caption1>
    </div>
  );
};
